import { FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GroupSettingsContext } from "../../../contexts/GroupSettingsContext";
import { updateDashboardFunc } from "../../../lib/helper";
import theme from "../../../theme";
import { ColorOptions } from "../../../theme/colors";
import { Dashboard } from "../../../types/files";

const useStyles = makeStyles((theme) => ({
    title: {
        fontFamily: 'Poppins',
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#666666',
    },
    formContainer: {
        minWidth: '105px',
        padding: 0,
        border: 0,
        "& > .MuiInputBase-root":{
            height: '30px',
            width: 'auto',
            borderRadius: '32px',
            "& > .MuiSvgIcon-root": {
                width: 28,
                height: 28,
            },
            "& fieldset": {
                border: 0,
            },
        },
    },
    menuItem: {
        width: '100%',
    },
    badge: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '30px',
        width: 'auto',
        padding: '0 8px',
        borderRadius: '32px',
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: '500',
        textTransform: 'capitalize',
    },
}));

const DashboardStatusMenu: FC<{
    dashboard: Dashboard,
    withTitle?: boolean,
    readOnly?: boolean,
}> = ({ dashboard, withTitle, readOnly }) => {
    const classes = useStyles();
    const { statuses } = useContext(GroupSettingsContext);
    const [selected, setSelected] = useState<string>('');

    const palettes = useMemo(() => Array.from(new Set(Object.values(ColorOptions)
        .filter(color => !['neutral', 'brown', 'lime', 'pink', 'red', 'shades'].includes(color)))), []);

    const getColors = useCallback((title: string) => {
        const index = statuses.findIndex((s) => s.toLowerCase() === title.toLowerCase());

        if ((index !== -1) && (index < palettes.length)) {
            return ({
                backgroundColor: theme.colors[palettes[index]][100],
                color: theme.colors[palettes[index]][800],
            });
        }

        return ({
            backgroundColor: theme.colors.neutral[100],
            color: theme.colors.neutral[600],
        });
    }, [statuses, palettes]);

    const handleChange = useCallback(async (newStatus: string) => {
        const { tagGroups, status, ...props } = dashboard;

        setSelected(newStatus);
        await updateDashboardFunc({ ...props,
            status: newStatus === dashboard.status ? '<rename this status>' : newStatus,
            tagGroups: tagGroups?.map(({title, tags}) => ({title, tags})) ?? [],
        });
    }, [dashboard]);

    useEffect(()=>{
        setSelected(dashboard.status);
    }, [dashboard.status]);

    return (<>
        <Stack direction="column" spacing={1} width="fit-content" height="fit-content">
            {withTitle && (<Typography className={classes.title}> {'Status'} </Typography>)}
            {readOnly ? (
                <Typography className={classes.badge}
                    sx={{ ...getColors(selected) }}>
                    {selected || 'N/A'}
                </Typography>
            ) : (<>
                <FormControl className={classes.formContainer} variant="outlined"
                    sx={{ "& > .MuiInputBase-root":{ ...getColors(selected) } }}>
                    <Select value={selected}
                        onChange={(e) => handleChange(e.target.value as string)}
                        renderValue={(selected: string) => (
                            <Typography className={classes.badge}
                                sx={{ ...getColors(selected) }}> {selected} </Typography>
                        )}
                        MenuProps={{ PaperProps: { sx: { borderRadius: 5 }} } }>
                        {statuses.map((item, i) => (
                            <MenuItem className={classes.menuItem}
                                value={item}
                                sx={(dashboard?.status?.toLowerCase() === item) ? { background: `${theme.colors.cyan['50']} !important` } : {}}
                                key={'dashboard-status-menu-170-' + i}>
                                <Typography className={classes.badge}
                                    sx={{ ...getColors(item) }}> {item} </Typography>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </>)}
        </Stack>
    </>);
}

export default DashboardStatusMenu;